import { useRef, useState } from "react";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ChevronDown,
  ChevronUp,
  FilterIcon,
} from "../icons";
import { TextField } from "../blocks/fields/TextField";
import { BooleanField } from "../blocks/fields/BooleanField";
import { DateField } from "../blocks/fields/DateField";
import { NumberField } from "../blocks/fields/NumberField";
import { FieldValue } from "../blocks/fields/FieldValue";
import { PicklistField } from "../blocks/fields/PicklistField";
import { Button } from "../elements/Button";
import { useDismissOnOutsideClick } from "../hooks/useDismissOnOutsideClick";
import "./ListFilterBar.scss";
import {
  ANY_TIME_LABEL,
  DATE_RANGE_PRESET_OPTIONS,
  presetRangeValues,
  typedRangeValues,
} from "./dateRangePresets";
import { hasFilterValue } from "./listFilterValues";
import {
  ListFilterDefinition,
  dateKey,
  fromKey,
  operatorKey,
  presetKey,
  toKey,
} from "./listFilters.types";
import {
  DefaultNumberFilterOperator,
  NUMBER_FILTER_OPERATOR_OPTIONS,
  toNumberFilterOperator,
} from "./numberFilterOperators";
import { usePortalUserOptions } from "../hooks/usePortalUserOptions";
import { ListLookupFilter } from "./ListLookupFilter";
import { FilterBarProps } from "./FilterBar.types";

const STATUS_OPTIONS = [
  { Key: "active", Value: "Active" },
  { Key: "inactive", Value: "Inactive" },
];

const YES_NO_OPTIONS = [
  { Key: "yes", Value: "Yes" },
  { Key: "no", Value: "No" },
];

/**
 * A band of filters, for a list or a related table. Built from the page components' own
 * field controls, so a filter box looks and behaves like a field on a record page rather
 * than like the legacy table's.
 *
 * The filters people actually start from are on show, and the rest are behind Advanced
 * filters - which opens itself when a hidden filter arrives with a value, so a shared
 * link never narrows the list for a reason it does not show.
 */
export function FilterBar({
  filters,
  filterValues,
  filterOptions = {},
  setFilterValue,
  setFilterValues,
  clearFilters,
  status,
  submit,
  description,
}: FilterBarProps) {
  const usesSubmit = Boolean(submit);
  const portalUsers = usePortalUserOptions(
    filters.some((filter) => filter.optionsFrom === "portal-users")
  );
  const advancedFilters = filters.filter((filter) => filter.advanced);
  const [advancedOpen, setAdvancedOpen] = useState(() =>
    advancedFilters.some((filter) => hasFilterValue(filter, filterValues))
  );
  // The whole band, so it holds the toggle as well as the panel hanging off it -
  // pressing the toggle to close it must not count as pressing outside.
  const bandRef = useRef<HTMLDivElement>(null);
  const closeAdvanced = () => setAdvancedOpen(false);
  useDismissOnOutsideClick(bandRef, advancedOpen, closeAdvanced);

  const advancedCount = advancedFilters.filter((filter) =>
    hasFilterValue(filter, filterValues)
  ).length;

  const day = (key: string) => (filterValues[key] as string) ?? "";

  const filterClass = (filter: ListFilterDefinition, extra = "") =>
    classNames(
      "list-filter-bar__filter",
      filter.width && `list-filter-bar__filter--${filter.width}`,
      extra
    );

  const renderDateRange = (filter: ListFilterDefinition) => (
    <div
      className={filterClass(filter, "list-filter-bar__filter--range")}
      key={filter.name}
    >
      <PicklistField
        label={filter.label}
        name={presetKey(filter.name)}
        value={day(presetKey(filter.name))}
        options={DATE_RANGE_PRESET_OPTIONS}
        placeholder={ANY_TIME_LABEL}
        helpText={filter.helpText}
        // A range only ever fills the two dates below it; they are what filters.
        onChange={(value) =>
          setFilterValues(
            presetRangeValues(filter.name, value ?? "", filterValues)
          )
        }
      />
      {[
        { key: fromKey(filter.name), edge: "From" },
        { key: toKey(filter.name), edge: "To" },
      ].map(({ key, edge }) => (
        <DateField
          key={key}
          label={`${filter.label} ${edge}`}
          name={key}
          value={day(key)}
          // Typing a day of your own is what Custom means, so the select follows.
          onChange={(value) =>
            setFilterValues(
              typedRangeValues(filter.name, key, value, filterValues)
            )
          }
        />
      ))}
    </div>
  );

  const renderDay = (filter: ListFilterDefinition) => (
    <div
      className={filterClass(filter, "list-filter-bar__filter--range")}
      key={filter.name}
    >
      <DateField
        label={filter.label}
        name={dateKey(filter.name)}
        value={day(dateKey(filter.name))}
        helpText={filter.helpText}
        onChange={(value) => setFilterValue(dateKey(filter.name), value ?? "")}
      />
      {[
        { key: fromKey(filter.name), edge: "From" },
        { key: toKey(filter.name), edge: "To" },
      ].map(({ key, edge }) => (
        <FieldValue key={key} label={`${edge} Time`} htmlFor={key}>
          <input
            id={key}
            name={key}
            type="time"
            className="form-control"
            value={day(key)}
            onChange={(event) => setFilterValue(key, event.target.value)}
          />
        </FieldValue>
      ))}
    </div>
  );

  const renderFilter = (filter: ListFilterDefinition) => {
    if (filter.kind === "day") {
      return renderDay(filter);
    }

    if (filter.kind === "text") {
      return (
        <div className={filterClass(filter)} key={filter.name}>
          <TextField
            label={filter.label}
            name={filter.name}
            value={(filterValues[filter.name] as string) ?? ""}
            placeholder={filter.placeholder}
            helpText={filter.helpText}
            onChange={(value) => setFilterValue(filter.name, value)}
          />
        </div>
      );
    }

    if (filter.kind === "number") {
      const held = filterValues[filter.name];

      return (
        <div
          className={filterClass(filter, "list-filter-bar__filter--range")}
          key={filter.name}
        >
          <PicklistField
            label={`${filter.label} Operator`}
            name={operatorKey(filter.name)}
            value={toNumberFilterOperator(filterValues[operatorKey(filter.name)])}
            options={NUMBER_FILTER_OPERATOR_OPTIONS}
            hidePlaceholder
            onChange={(value) =>
              setFilterValue(
                operatorKey(filter.name),
                !value || value === DefaultNumberFilterOperator ? "" : value
              )
            }
          />
          <NumberField
            label={filter.label}
            name={filter.name}
            value={typeof held === "string" && held !== "" ? Number(held) : null}
            placeholder={filter.placeholder}
            helpText={filter.helpText}
            onChange={(value) =>
              setFilterValue(filter.name, value === null ? "" : String(value))
            }
          />
        </div>
      );
    }

    if (filter.kind === "boolean" && filter.toggle) {
      return (
        <div className={filterClass(filter)} key={filter.name}>
          <BooleanField
            label={filter.label}
            name={filter.name}
            layout="stacked"
            value={Boolean(filterValues[filter.name])}
            helpText={filter.helpText}
            onChange={(value) => setFilterValue(filter.name, value)}
          />
        </div>
      );
    }

    if (filter.kind === "boolean") {
      return (
        <div className={filterClass(filter)} key={filter.name}>
          {/* A dropdown rather than a switch, so it reads as one of the filters beside
              it. No is off, which is not a filter at all. */}
          <PicklistField
            label={filter.label}
            name={filter.name}
            value={filterValues[filter.name] ? "yes" : "no"}
            options={YES_NO_OPTIONS}
            hidePlaceholder
            helpText={filter.helpText}
            onChange={(value) => setFilterValue(filter.name, value === "yes")}
          />
        </div>
      );
    }

    if (filter.kind === "picklist") {
      return (
        <div className={filterClass(filter)} key={filter.name}>
          <PicklistField
            label={filter.label}
            name={filter.name}
            value={(filterValues[filter.name] as string) ?? ""}
            options={
              (filter.optionsFrom === "portal-users"
                ? portalUsers
                : filter.optionsFromRows
                  ? filterOptions[filter.name]
                  : filter.options) ?? []
            }
            placeholder={filter.placeholder ?? "Any"}
            helpText={filter.helpText}
            controlClassName="min-w-0 text-truncate"
            onChange={(value) => setFilterValue(filter.name, value ?? "")}
          />
        </div>
      );
    }

    if (filter.kind === "lookup") {
      return (
        <div className={filterClass(filter)} key={filter.name}>
          <ListLookupFilter
            filter={filter}
            value={(filterValues[filter.name] as string) ?? ""}
            onChange={(value) => setFilterValue(filter.name, value)}
          />
        </div>
      );
    }

    return renderDateRange(filter);
  };

  return (
    <div className="list-filter-bar px-3 py-2 px-md-4" ref={bandRef}>
      <div className="list-filter-bar__row">
        {filters
          .filter((filter) => !filter.advanced)
          .map((filter) => renderFilter(filter))}
        {status && (
          <div className="list-filter-bar__filter">
            <PicklistField
              label="Status"
              name="status"
              value={status.showInactive ? "inactive" : "active"}
              options={STATUS_OPTIONS}
              // A list is always showing one status or the other, so there is no
              // empty option to offer.
              hidePlaceholder
              onChange={(value) => status.setShowInactive(value === "inactive")}
            />
          </div>
        )}
        <div className="list-filter-bar__actions">
          {advancedFilters.length > 0 && (
            <>
              <button
                type="button"
                className="list-filter-bar__advanced-toggle btn border rounded-2 bg-white text-dark fw-medium hover-bg-light text-nowrap"
                aria-expanded={advancedOpen}
                onClick={() => setAdvancedOpen((open) => !open)}
              >
                <FontAwesomeIcon icon={FilterIcon} className="me-2" />
                {advancedCount > 0
                  ? `Advanced filters (${advancedCount})`
                  : "Advanced filters"}
                <FontAwesomeIcon
                  icon={advancedOpen ? ChevronUp : ChevronDown}
                  className="ms-2 d-none d-md-inline-block"
                />
              </button>
              {advancedOpen && (
                <>
                  <div
                    className="list-filter-bar__advanced-backdrop d-md-none"
                    onClick={closeAdvanced}
                  />
                  <div
                    className="list-filter-bar__advanced"
                    role="dialog"
                    aria-label="Advanced filters"
                  >
                    <div className="d-flex d-md-none align-items-center justify-content-between mb-3">
                      <h3 className="modal-title">Advanced filters</h3>
                      <button
                        type="button"
                        className="btn btn-close"
                        aria-label="Close"
                        onClick={closeAdvanced}
                      />
                    </div>
                    <div className="list-filter-bar__filters">
                      {advancedFilters.map((filter) => renderFilter(filter))}
                    </div>
                    <div className="d-md-none mt-3">
                      <Button
                        text="Done"
                        variant="primary"
                        isBlock
                        onClick={closeAdvanced}
                      />
                    </div>
                  </div>
                </>
              )}
            </>
          )}
          {!usesSubmit && (
            <button
              type="button"
              className="btn border rounded-2 bg-white text-dark fw-medium hover-bg-light text-nowrap"
              onClick={clearFilters}
            >
              Clear
            </button>
          )}
          {submit && (
            <>
              <button
                type="button"
                className="btn border rounded-2 bg-white text-dark fw-medium hover-bg-light"
                disabled={submit.loading}
                onClick={clearFilters}
              >
                Clear
              </button>
              {/* The footers' button, so a search reads like a save: the label
                  fades and a spinner sits over it. */}
              <Button
                text="Search"
                className="entity-page__footer-btn--primary rounded-2 fw-medium"
                disabled={submit.loading}
                showSpinner={submit.isSearching}
                onClick={submit.submitFilters}
              />
            </>
          )}
        </div>
      </div>
      {description && <p className="fs-7 fst-italic mt-2 mb-0">{description}</p>}
    </div>
  );
}

export default FilterBar;
